import React, { useEffect, useState } from "react";
import { popsApi, fmtUSD } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Users, BookMarked, BadgePercent, Wallet, KeyRound } from "lucide-react";
import { toast } from "sonner";

function PortalAccessCard({ config }) {
  const [users, setUsers] = useState([]);
  const [dlg, setDlg] = useState(null); // {customer, existing}
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const load = () => popsApi.portalUsers().then(setUsers).catch(console.error);
  useEffect(() => { load(); }, []);

  const open = (customer) => {
    const existing = users.find((u) => u.customer_id === customer.id);
    setDlg({ customer, existing });
    setEmail(existing?.email || "");
    setPassword("");
  };

  const save = async () => {
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      if (dlg.existing && email === dlg.existing.email) {
        await popsApi.resetPortalUser(dlg.existing.id, password);
        toast.success(`Portal password reset for ${email}`);
      } else {
        await popsApi.createPortalUser(dlg.customer.id, email, password);
        toast.success(`Portal access ${dlg.existing ? "updated" : "created"} for ${dlg.customer.name}`, {
          description: `Login at /portal/login with ${email}`,
        });
      }
      setDlg(null);
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="cs-card p-5" data-testid="portal-access-card">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="w-4 h-4 text-[#00579e]" />
        <h3 className="cs-heading text-sm font-semibold">Customer Portal Access</h3>
        <span className="text-[11px] text-muted-foreground ml-auto">
          white-labeled login at <span className="cs-mono">/portal/login</span> — each customer sees only their invoices & reports
        </span>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
          <tr className="text-left">
            {["Customer", "Portal login", "Created", "Last login", ""].map((h) => (
              <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {config.customers.map((c) => {
            const u = users.find((x) => x.customer_id === c.id);
            return (
              <tr key={c.id} className="border-b border-border last:border-0" data-testid={`portal-user-row-${c.id}`}>
                <td className="px-3 py-2 font-semibold">{c.name}</td>
                <td className="px-3 py-2 cs-mono">{u ? u.email : <span className="text-muted-foreground">no access</span>}</td>
                <td className="px-3 py-2 text-muted-foreground">{u ? (u.created_at || "").slice(0, 10) : "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{u?.last_login ? u.last_login.slice(0, 16).replace("T", " ") : "never"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => open(c)} data-testid={`portal-manage-${c.id}`}
                    className="text-[11px] text-[#00579e] font-semibold hover:underline">
                    {u ? "reset / change" : "create access"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="rounded-sm border-border" data-testid="portal-user-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Portal access — {dlg?.customer?.name}</DialogTitle>
            <DialogDescription>
              {dlg?.existing ? "Change the login email or reset the password." : "Create a portal login for this customer's billing contact."}
            </DialogDescription>
          </DialogHeader>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="billing@customer.com"
            data-testid="portal-user-email-input" className="rounded-sm" />
          <Input value={password} onChange={(e) => setPassword(e.target.value)} type="text" placeholder="New password (min 6 chars)"
            data-testid="portal-user-password-input" className="rounded-sm" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDlg(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="portal-user-save"
              className="rounded-sm bg-[#00579e] hover:bg-[#004580] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function RebillingTab({ config, onChange }) {
  const [newCharge, setNewCharge] = useState({ customer_id: "", type: "fixed", name: "", amount: "", pct: "", rate: "", unit: "unit" });
  const [chargeMod, setChargeMod] = useState("");
  useEffect(() => { popsApi.chargeModifier().then((m) => setChargeMod(String(m.pct))).catch(console.error); }, []);

  const saveModifier = async () => {
    try {
      const m = await popsApi.setChargeModifier(parseFloat(chargeMod) || 0);
      toast.success(`Global charge modifier set to ${m.pct > 0 ? "+" : ""}${m.pct}%`, {
        description: "Applied to every custom charge line at the next invoice run.",
      });
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const setPb = async (cid, pbId) => {
    try {
      await popsApi.setPriceBook(cid, pbId);
      toast.success("Price book assigned — next invoice run re-rates with the new book");
      onChange();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const addCharge = async () => {
    if (!newCharge.customer_id || !newCharge.name) {
      toast.error("Customer and charge name are required");
      return;
    }
    try {
      const payload = { ...newCharge };
      ["amount", "pct", "rate"].forEach((k) => { payload[k] = payload[k] ? parseFloat(payload[k]) : null; });
      await popsApi.addCharge(payload);
      toast.success("Charge created — will render as a visible, auditable invoice line");
      setNewCharge({ customer_id: "", type: "fixed", name: "", amount: "", pct: "", rate: "", unit: "unit" });
      onChange();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const suppress = async (charge) => {
    const reinstate = !!charge.suppressed;
    try {
      await popsApi.suppressCharge(charge.id, reinstate ? "reinstate" : "all_future", reinstate ? "reinstated from UI" : "suppressed from UI");
      toast.success(reinstate ? "Charge reinstated" : "Charge suppressed (reversible, fully logged)");
      onChange();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4" data-testid="pops-rebilling-tab">
      {/* Customers */}
      <div className="cs-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Customers</h3>
          <span className="text-[11px] text-muted-foreground ml-auto">customer object decoupled from cloud accounts (FR-1.2)</span>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Customer", "Legal entity", "Projects", "Currency", "Terms", "PO", "Price book"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {config.customers.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0" data-testid={`customer-row-${c.id}`}>
                <td className="px-3 py-2 font-semibold">{c.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.legal_entity}</td>
                <td className="px-3 py-2 cs-mono text-[11px]">{(c.projects || []).join(", ")}</td>
                <td className="px-3 py-2 cs-mono">{c.currency}</td>
                <td className="px-3 py-2">{c.payment_terms}</td>
                <td className="px-3 py-2 cs-mono text-muted-foreground">{c.po_number}</td>
                <td className="px-3 py-2">
                  <Select value={c.price_book_id} onValueChange={(v) => setPb(c.id, v)}>
                    <SelectTrigger className="rounded-sm h-7 text-[11px] w-[150px]" data-testid={`pb-select-${c.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config.price_books.filter((p) => p.tenant_id === "t-l2").map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name} v{p.version}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Price books */}
      <div className="cs-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <BookMarked className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Price Books (versioned, effective-dated, inheritable)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {config.price_books.map((p) => (
            <div key={p.id} className="border border-border rounded-sm p-3" data-testid={`pricebook-${p.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{p.name}</span>
                <span className="cs-mono text-[10px] text-muted-foreground">v{p.version}</span>
                <span className={`ml-auto px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-semibold ${p.tenant_id === "t-l1" ? "bg-[#0B2545] text-white" : "bg-[#e0f2fe] text-[#0369a1]"}`}>
                  {p.tenant_id === "t-l1" ? "L1 distributor" : "L2 MSP"}
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground mb-2">effective {p.effective_from}{p.inherits_from ? ` · inherits ${p.inherits_from}` : ""}</div>
              {p.rules.map((r, i) => (
                <div key={i} className="flex items-center text-[11px] py-0.5 border-t border-border/60 first:border-0">
                  <span className="cs-mono">{r.scope}</span>
                  <span className={`ml-auto cs-mono font-semibold ${r.pct < 0 ? "text-[#b91c1c]" : "text-[#15803d]"}`}>{r.pct > 0 ? "+" : ""}{r.pct}%</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Charges */}
      <div className="cs-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <BadgePercent className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Custom Charges</h3>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Global modifier (± % on all charges)</span>
            <Input value={chargeMod} onChange={(e) => setChargeMod(e.target.value)} type="number"
              data-testid="charge-modifier-input" className="rounded-sm h-7 text-xs w-[70px]" />
            <Button size="sm" variant="outline" onClick={saveModifier} data-testid="charge-modifier-save"
              className="rounded-full h-7 text-[11px]">Apply</Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4 items-end">
          <Select value={newCharge.customer_id} onValueChange={(v) => setNewCharge({ ...newCharge, customer_id: v })}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="charge-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent>{config.customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={newCharge.type} onValueChange={(v) => setNewCharge({ ...newCharge, type: v })}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="charge-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["fixed", "percent", "per_unit", "per_usage_hour"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={newCharge.name} onChange={(e) => setNewCharge({ ...newCharge, name: e.target.value })}
            placeholder="Charge name" data-testid="charge-name-input" className="rounded-sm h-9 text-xs col-span-2" />
          {newCharge.type === "fixed" && (
            <Input value={newCharge.amount} onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
              type="number" placeholder="$ / period" data-testid="charge-amount-input" className="rounded-sm h-9 text-xs" />
          )}
          {newCharge.type === "percent" && (
            <Input value={newCharge.pct} onChange={(e) => setNewCharge({ ...newCharge, pct: e.target.value })}
              type="number" placeholder="% of subtotal" data-testid="charge-pct-input" className="rounded-sm h-9 text-xs" />
          )}
          {(newCharge.type === "per_unit" || newCharge.type === "per_usage_hour") && (
            <Input value={newCharge.rate} onChange={(e) => setNewCharge({ ...newCharge, rate: e.target.value })}
              type="number" placeholder={newCharge.type === "per_usage_hour" ? "$ / usage-hr" : "$ / unit"}
              data-testid="charge-rate-input" className="rounded-sm h-9 text-xs" />
          )}
          <Button onClick={addCharge} data-testid="add-charge-button" className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs">
            Add charge
          </Button>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Charge", "Customer", "Type", "Value", "Recurring", "Start", "Status", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {config.charges.map((ch) => (
              <tr key={ch.id} className={`border-b border-border last:border-0 ${ch.suppressed ? "opacity-60" : ""}`} data-testid={`charge-row-${ch.id}`}>
                <td className="px-3 py-2 font-medium">{ch.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{(config.customers.find((c) => c.id === ch.customer_id) || {}).name}</td>
                <td className="px-3 py-2 cs-mono text-[10px] uppercase">{ch.type}</td>
                <td className="px-3 py-2 cs-mono">
                  {ch.type === "fixed" && fmtUSD(ch.amount)}
                  {ch.type === "percent" && `${ch.pct}%${ch.min_usd ? ` (min ${fmtUSD(ch.min_usd)})` : ""}`}
                  {ch.type === "per_unit" && `${fmtUSD(ch.rate)}/${ch.unit}`}
                  {ch.type === "tiered" && (ch.tiers || []).map((t) => `${t.pct}%`).join(" / ")}
                </td>
                <td className="px-3 py-2">{ch.recurring ? "yes" : "one-time"}</td>
                <td className="px-3 py-2 cs-mono text-muted-foreground">{ch.start}</td>
                <td className="px-3 py-2">
                  {ch.suppressed
                    ? <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-[#fee2e2] text-[#b91c1c]" title={ch.suppressed.reason}>suppressed</span>
                    : <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-[#dcfce7] text-[#15803d]">active</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => suppress(ch)} data-testid={`suppress-${ch.id}`}
                    className="text-[11px] text-[#00579e] hover:underline">
                    {ch.suppressed ? "reinstate" : "suppress"}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await popsApi.deleteCharge(ch.id);
                        toast.success("Charge deleted");
                        onChange();
                      } catch (e) {
                        toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
                      }
                    }}
                    data-testid={`delete-charge-${ch.id}`}
                    className="text-[11px] text-[#b91c1c] hover:underline ml-3"
                  >
                    delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Credits */}
      <div className="cs-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Credits (provider & MSP-issued · partial application · carry-forward)</h3>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {["Credit", "Customer", "Source", "Original", "Remaining", "Expiry"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {config.credits.map((cr) => (
              <tr key={cr.id} className="border-b border-border last:border-0" data-testid={`credit-row-${cr.id}`}>
                <td className="px-3 py-2 font-medium">{cr.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{(config.customers.find((c) => c.id === cr.customer_id) || {}).name}</td>
                <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${cr.source === "provider" ? "bg-[#e0f2fe] text-[#0369a1]" : "bg-[#ede9fe] text-[#6d28d9]"}`}>{cr.source}</span></td>
                <td className="px-3 py-2 cs-mono">{fmtUSD(cr.amount)}</td>
                <td className="px-3 py-2 cs-mono font-semibold text-[#15803d]">{fmtUSD(cr.remaining)}</td>
                <td className="px-3 py-2 cs-mono text-muted-foreground">{cr.expiry || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Portal access */}
      <PortalAccessCard config={config} />
    </div>
  );
}
